import _ from 'underscore';
import { AppType } from '../../utils';
import util from "../../utils/es5Utils";

// Import version configuration
import { getBlocksForVersion, getAvailableVersions, isVersionAtLeast } from './blockVersionConfig';
import { getAllTypeWidgetsFromContext, getAllVariablesFromContext, getAllXSightTopicsFromContext } from "./blockVersions/v1/helpers";
export default class ToolboxList {
    // Factory entry point. A constructor can't be used here since the result
    // is a plain block map, not a ToolboxList instance (see S6635).
    static build(props) {
        return new ToolboxList()._getToolboxList(props);
    }

    _getSearchFields(widgetsArr) {
        return _.chain(widgetsArr).flatten().reject(item => !_.isString(item)).value();
    }

    _getLibraryBlocks(library) {
        if (!library.blocks) return {};
        return library.blocks.reduce((allBlocks, block) => {
            allBlocks[block.name] = {
                component: block,
                searchTags: [...block.keywords.split(","), block.name]
            };
            return allBlocks;
        }, {});
    }

    // removes a single top-level excluded block, either by direct name match
    // or, if no direct match exists, by version-aware pattern match (e.g.
    // "SnapActionSubmit" also matches "SnapActionSubmitV2")
    _removeTopLevelExclusion(categoryBlocks, blockName, excludedBlockNames) {
        if (categoryBlocks[blockName]) {
            // Direct match - exclude this specific block
            excludedBlockNames.push(categoryBlocks[blockName].component.blockName);
            delete categoryBlocks[blockName];
            return;
        }
        // Pattern-based exclusion - exclude all versions of this block
        // Regex matches: blockName, blockNameV2, blockNameV3, etc.
        const pattern = new RegExp(String.raw`^${blockName}(V\d+)?$`);
        const blocksToRemove = Object.keys(categoryBlocks).filter(key => pattern.test(key));

        for (const matchingBlock of blocksToRemove) {
            excludedBlockNames.push(categoryBlocks[matchingBlock].component.blockName);
            delete categoryBlocks[matchingBlock];
        }
    }

    // remove any top-level blocks to be excluded
    _removeArrayExclusions(categoryBlocks, excludedBlocks, excludedBlockNames) {
        for (const blockName of excludedBlocks) {
            // Version-aware exclusion: "SnapActionSubmit" excludes SnapActionSubmit, SnapActionSubmitV2, etc.
            this._removeTopLevelExclusion(categoryBlocks, blockName, excludedBlockNames);
        }
    }

    // remove any blocks nested in childComponents to be excluded (i.e. in Widget Values)
    _removeNestedExclusions(categoryBlocks, excludedBlocks, excludedBlockNames) {
        for (const [nestedCategory, excludedChildBlocks] of Object.entries(excludedBlocks)) {
            const childBlocks = categoryBlocks[nestedCategory];

            for (const childBlockName of excludedChildBlocks) {
                const childBlock = childBlocks.childComponents[childBlockName];
                if (childBlock) {
                    excludedBlockNames.push(childBlock.component.blockName);
                    delete childBlocks.childComponents[childBlockName];
                }
            }
        }
    }

    // modifies allBlocks in place to remove any blocks that should be excluded based on the app type
    _filterBlocksOnAppType(allBlocks, blocksToExcludeByAppType, appTypeId) {
        const exclusions = blocksToExcludeByAppType[appTypeId];
        if (!exclusions) return;

        const excludedBlockNames = [];

        for (const [category, excludedBlocks] of Object.entries(exclusions)) {
            const categoryBlocks = allBlocks[category];

            if (Array.isArray(excludedBlocks)) {
                this._removeArrayExclusions(categoryBlocks, excludedBlocks, excludedBlockNames);
            } else if (typeof excludedBlocks === 'object') {
                this._removeNestedExclusions(categoryBlocks, excludedBlocks, excludedBlockNames);
            }
        }

        this._filterBlocksInMyLibraries(allBlocks, excludedBlockNames);
    }

    // filter out the saved blocks in My Libraries that should be excluded because a nested block has been excluded
    _filterBlocksInMyLibraries(allBlocks, excludedBlockNames) {
        const parser = new DOMParser();

        for (const libraryContents of Object.values(allBlocks['My Libraries'])) {
            const blocksToExclude = [];

            for (const [childBlockName, childBlockComponent] of Object.entries(libraryContents.childComponents)) {
                const blockXmlContents = childBlockComponent.component.xml;
                if (!blockXmlContents) continue;

                const blockXml = parser.parseFromString(blockXmlContents, 'text/xml');
                const blockAndShadowElements = blockXml.querySelectorAll('block, shadow');

                for (const element of blockAndShadowElements) {
                    const nestedBlockType = element.getAttribute('type');
                    if (excludedBlockNames.includes(nestedBlockType)) {
                        blocksToExclude.push(childBlockName);
                        break; // stop parsing current block as it has been marked for deletion
                    }
                }
            }

            for (const block of blocksToExclude) {
                delete libraryContents.childComponents[block];
            }

            libraryContents.hasExcludedBlocks = blocksToExclude.length > 0;
        }
    }

    // registers a single block (and any of its nested child components)
    _registerBlock(props, blockName, blockDef) {
        // add widgets to props (required for blocks that reference widgets)
        let widgets = [];
        try {
            widgets = util.widgetsOfType ? util.widgetsOfType(props.context, blockName) : [];
        } catch {
            widgets = [];
        }
        const baseProps = {
            ...props,
            widgets
        };

        // register top-level blocks
        if (typeof blockDef.component === 'function') {
            try {
                if (blockDef.component.prototype && (blockDef.component.prototype.isReactComponent || blockDef.component.prototype.render)) {
                    // this is a class component - need keyword "new" to initialize
                    new blockDef.component(baseProps);
                } else if (!blockDef.childComponents) { // skip widget category init - it is not needed here
                    blockDef.component(baseProps);
                }
            } catch (e) {
                console.warn(`Error registering block "${blockName}":`, e);
            }
        }

        // register child blocks in Widget Values
        if (!blockDef.childComponents) {
            return;
        }
        for (const [childName, childDef] of Object.entries(blockDef.childComponents)) {
            if (typeof childDef.component !== 'function') {
                continue;
            }
            try {
                childDef.component(baseProps);
            } catch (e) {
                console.warn(`Error registering block "${childName}":`, e);
            }
        }
    }

    _registerAllBlocks(props, allBlocks) {
        for (const blocks of Object.values(allBlocks)) {
            for (const [blockName, blockDef] of Object.entries(blocks)) {
                this._registerBlock(props, blockName, blockDef);
            }
        }
    }

    // Intelligently add dynamic search tags to version-loaded blocks
    _addDynamicSearchTags(allBlocks, dynamicTags) {
        // Define mapping rules for different types of dynamic tags
        const tagMappings = [
            // Environment Variables
            { 
                category: "Global Variables", 
                blocks: ["SetEnvironmentVariable", "GetEnvironmentVariable"], 
                tags: dynamicTags.envVariables 
            },
            // XSight Topics
            { 
                category: "XSight Topics", 
                blocks: ["SetXSightTopic", "GetXSightTopic"], 
                tags: dynamicTags.xSightTopics 
            }
        ];

        // Widget Values - automatically map widget arrays to their corresponding blocks
        if (allBlocks["Widget Values"]) {
            const widgetMappings = {
                barcode: dynamicTags.barcodeWidgets,
                button: dynamicTags.buttonWidgets,
                checkbox: dynamicTags.checkboxWidgets,
                checkboxlist: dynamicTags.checkboxListWidgets,
                chart: dynamicTags.chartWidgets,
                timer: dynamicTags.timerWidgets,
                date: dynamicTags.dateWidgets,
                dropdown: dynamicTags.dropdownWidgets,
                label: dynamicTags.labelWidgets,
                layout: dynamicTags.layoutWidgets,
                paragraph: dynamicTags.paragraphWidgets,
                rating: dynamicTags.ratingWidgets,
                radiobuttonlist: dynamicTags.radioListWidgets,
                radiobutton: dynamicTags.radioWidgets,
                section: dynamicTags.sectionWidgets,
                image: dynamicTags.imageWidgets,
                fileupload: dynamicTags.fileUploadWidgets,
                textbox: dynamicTags.textboxWidgets,
                tableview: dynamicTags.tableWidgets,
                flipswitch: dynamicTags.flipSwitchWidgets,
                map: dynamicTags.mapWidgets,
                userlist: dynamicTags.userListWidgets,
                businesscard: dynamicTags.businessCardWidgets,
                grouplist: dynamicTags.groupListWidgets
            };

            // Apply widget tags to all child components of each widget type
            Object.entries(widgetMappings).forEach(([widgetType, widgetTags]) => {
                if (allBlocks["Widget Values"][widgetType]?.childComponents && widgetTags) {
                    Object.values(allBlocks["Widget Values"][widgetType].childComponents).forEach(childBlock => {
                        if (childBlock.searchTags) {
                            childBlock.searchTags = [...childBlock.searchTags, ...widgetTags];
                        }
                    });
                }
            });
        }

        // Apply standard category mappings
        tagMappings.forEach(({ category, blocks, tags }) => {
            if (allBlocks[category] && tags) {
                blocks.forEach(blockName => {
                    if (allBlocks[category][blockName]?.searchTags) {
                        allBlocks[category][blockName].searchTags = [
                            ...allBlocks[category][blockName].searchTags,
                            ...tags
                        ];
                    }
                });
            }
        });
    }

    _getToolboxList(props) {
        // Get ServerBuildInfo from context
        const serverBuildInfo = props.context?.ServerBuildInfo;
        
        const barcodeWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "barcode"));
        const buttonWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "button"));
        const checkboxWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "checkbox"));
        const checkboxListWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "checkboxlist"));
        const chartWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "chart"));
        const timerWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "timer"));
        const dateWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "date"));
        const dropdownWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "dropdown"));
        const labelWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "label"));
        const layoutWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "layout"));
        const paragraphWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "paragraph"));
        const ratingWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "rating"));
        const radioListWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "radiobuttonlist"));
        const radioWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "radiobutton"));
        const sectionWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "section"));
        const imageWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "image"));
        const fileUploadWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "fileupload"));
        const textboxWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "textbox"));
        const tableWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "tableview"));
        const flipSwitchWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "flipswitch"));
        const mapWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "map"));
        const userListWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "userlist"));
        const businessCardWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "businesscard"));
        const groupListWidgets = this._getSearchFields(getAllTypeWidgetsFromContext(props.context, "grouplist"));
        const envVariables = this._getSearchFields(getAllVariablesFromContext(props.context));
        const xSightTopics = this._getSearchFields(getAllXSightTopicsFromContext(props.context));

        // blocks to exclude based on app type
        const blocksToExcludeByAppType = {
            [AppType.STANDARD]: {
                'Widget Values': {
                    'user': [
                        'GetUserEmail',
                        'GetUserField',
                        'GetUserFirstName',
                        'GetUserFullName',
                        'GetUserId',
                        'GetUserLastName',
                        'GetUserLoggedIn',
                        'GetUserSystemId'
                    ],
                    'userlist': [
                        'GetUserList'
                    ]
                },
                'MobiControl': [
                   'MobiControlExecuteMCLegacyScript',
                   'MobiControlRequestAdminMode',
                   'MobiControlExecuteMCJavaScript',
                   'MobiControlGetUserDetail',
                   'MobiControlIsUserLoggedIn'
                ]
            },
            [AppType.WORKFLOW]: {
                'App Actions': [
                    'SnapActionSync'
                ],
                'MobiControl': [
                    'MobiControlExecuteMCLegacyScript',
                    'MobiControlRequestAdminMode',
                    'MobiControlExecuteMCJavaScript',
                    'MobiControlGetUserDetail',
                    'MobiControlIsUserLoggedIn'
                ]
            },
            [AppType.LOCKDOWN]: {
                'Widget Values': {
                    'hamburger': [
                        'SetHamburgerToPage',
                        'GetHamburgerName',
                        'SetHamburgerName',
                        'GetHamburgerImage',
                        'SetHamburgerImage'
                    ]
                },
                'App Actions': [
                    'SnapActionResetPage',
                    'SnapActionSubmit',
                    'SnapActionValidate'
                ]
            }
        }

        // XSight Topics blocks in Lockdown are only excluded if Snap is below 2026.1.0
        if (!isVersionAtLeast(serverBuildInfo, '2026.1.0')) {
            blocksToExcludeByAppType[AppType.LOCKDOWN]['XSight Topics'] = [
                'GetXSightTopic',
                'SetXSightTopic',
            ];
        }

        // Get all blocks for the current version
        // All blocks are now managed by the version config system
        const versionBlocks = getBlocksForVersion(serverBuildInfo) || {};
        
        // Add My Libraries (which are not versioned)
        const allBlocks = {
            ...versionBlocks,
            "My Libraries": 
                props.libraries.reduce((allLibraries, library) => {
                    allLibraries[library.name] = {
                        childComponents: this._getLibraryBlocks(library),
                        hasExcludedBlocks: false
                    };
                    return allLibraries;
                }, {})
        }

        // Add dynamic search tags to version-loaded blocks
        this._addDynamicSearchTags(allBlocks, {
            // Environment and XSight variables
            envVariables,
            xSightTopics,
            // Widget arrays for Widget Values blocks
            barcodeWidgets, buttonWidgets, checkboxWidgets, checkboxListWidgets, chartWidgets, timerWidgets,
            dateWidgets, dropdownWidgets, labelWidgets, layoutWidgets, paragraphWidgets, ratingWidgets,
            radioListWidgets, radioWidgets, sectionWidgets, imageWidgets, fileUploadWidgets, textboxWidgets,
            tableWidgets, flipSwitchWidgets, mapWidgets, userListWidgets, businessCardWidgets, groupListWidgets
        });

        // Register all block versions for backward compatibility
        // (ensures saved scripts from any version can load without errors)
        for (const version of getAvailableVersions()) {
            this._registerAllBlocks(props, getBlocksForVersion(version));
        }

        // remove any blocks to be excluded from allBlocks in place
        this._filterBlocksOnAppType(allBlocks, blocksToExcludeByAppType, props.context.appTypeId);

        return allBlocks;
    }
}
